import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, FileText, Zap, CalendarCheck, Phone, Mail, Loader2 } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METIERS = [
  "Plombier", "Électricien", "Peintre", "Maçon", "Carreleur",
  "Menuisier", "Couvreur", "Chauffagiste", "Plaquiste", "Autre",
];

const initial = { nom: "", email: "", telephone: "", metier: "", formule: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email) {
      toast.error("Merci de renseigner votre nom et votre email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Message bien reçu — nous vous répondons sous 24h.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  const reassurance = [
    { icon: Lock, text: "Données confidentielles" },
    { icon: FileText, text: "Sans engagement" },
    { icon: Zap, text: "Réponse sous 24h" },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#1A2E4A] py-20 sm:py-28 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <div className="hidden md:block absolute right-0 top-0 bottom-0 w-1 bg-[#E8611A]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <span className="inline-block text-[#E8611A] font-semibold text-sm tracking-widest uppercase mb-4">
              Contact
            </span>
            <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Prêt à récupérer vos soirées&nbsp;?
            </h2>
            <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed">
              Réservez un appel découverte de 30 minutes. Gratuit, sans
              engagement, sans pression.
            </p>

            <a
              href={SITE.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta-calendly"
              className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm bg-[#E8611A] text-white font-semibold hover:bg-[#E8611A]/90 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#E8611A]/30"
            >
              <CalendarCheck className="w-5 h-5" />
              Réserver mon appel gratuit
            </a>

            <div className="mt-10 space-y-3">
              <a
                href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                data-testid="contact-phone"
                className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-white/10">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="text-base">{SITE.phone}</span>
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email"
                className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-white/10">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="text-base">{SITE.email}</span>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-3">
              {reassurance.map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex flex-col items-start gap-2 p-3 rounded-sm bg-white/5 border border-white/10">
                  <Icon className="w-5 h-5 text-[#E8611A]" strokeWidth={2.2} />
                  <span className="text-white/85 text-xs sm:text-sm font-medium leading-tight">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="contact-form"
              className="bg-white rounded-sm p-6 sm:p-8 lg:p-10 shadow-2xl shadow-black/30"
            >
              <h3 className="font-display font-bold text-[#1A2E4A] text-xl sm:text-2xl mb-1">
                Écrivez-nous directement
              </h3>
              <p className="text-[#8A8A8A] text-sm mb-6">
                Nous vous répondons sous 24h ouvrées.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nom *" name="nom" value={form.nom} onChange={handleChange} testid="input-nom" required />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={handleChange} testid="input-email" required />
                <Field label="Téléphone" name="telephone" type="tel" value={form.telephone} onChange={handleChange} testid="input-telephone" />
                <div>
                  <label className="block text-[#2D2D2D] text-sm font-semibold mb-1.5">Métier</label>
                  <select
                    name="metier"
                    value={form.metier}
                    onChange={handleChange}
                    data-testid="input-metier"
                    className="w-full px-3.5 py-3 rounded-sm border border-black/10 bg-white text-[#2D2D2D] text-sm focus:border-[#E8611A] focus:outline-none transition-colors"
                  >
                    <option value="">— Choisir —</option>
                    {METIERS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-[#2D2D2D] text-sm font-semibold mb-1.5">Formule qui vous intéresse</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "", l: "Pas encore sûr" },
                    { v: "Essentielle", l: "Essentielle" },
                    { v: "Sérénité", l: "Sérénité" },
                  ].map((opt) => (
                    <label
                      key={opt.l}
                      className={`cursor-pointer text-center px-3 py-2.5 rounded-sm border text-sm font-medium transition-all ${
                        form.formule === opt.v
                          ? "border-[#E8611A] bg-[#E8611A]/5 text-[#E8611A]"
                          : "border-black/10 text-[#2D2D2D] hover:border-[#1A2E4A]/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="formule"
                        value={opt.v}
                        checked={form.formule === opt.v}
                        onChange={handleChange}
                        className="sr-only"
                        data-testid={`radio-formule-${opt.v || "none"}`}
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-[#2D2D2D] text-sm font-semibold mb-1.5">Votre message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="input-message"
                  placeholder="Parlez-nous brièvement de votre activité et de vos besoins…"
                  className="w-full px-3.5 py-3 rounded-sm border border-black/10 bg-white text-[#2D2D2D] text-sm focus:border-[#E8611A] focus:outline-none transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm bg-[#E8611A] text-white font-semibold hover:bg-[#E8611A]/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  "Envoyer ma demande"
                )}
              </button>

              <p className="mt-4 text-[#8A8A8A] text-xs text-center">
                En soumettant ce formulaire, vous acceptez d&apos;être recontacté par {SITE.brand}.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <label className="block text-[#2D2D2D] text-sm font-semibold mb-1.5">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full px-3.5 py-3 rounded-sm border border-black/10 bg-white text-[#2D2D2D] text-sm focus:border-[#E8611A] focus:outline-none transition-colors"
      />
    </div>
  );
}
